// Обращения к API. Сессия — в httpOnly cookie, её выставляет сервер при входе.

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${url}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = Array.isArray(data.message) ? data.message.join('; ') : data.message;
    throw new ApiError(res.status, msg || `Ошибка ${res.status}`);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => call<T>('GET', url),
  post: <T>(url: string, body: unknown) => call<T>('POST', url, body),
  patch: <T>(url: string, body: unknown) => call<T>('PATCH', url, body),
  put: <T>(url: string, body: unknown) => call<T>('PUT', url, body),
  del: <T = void>(url: string) => call<T>('DELETE', url),
};

export type Role = 'owner' | 'admin' | 'finance' | 'manager' | 'dept_head' | 'viewer';

export interface Me {
  name: string;
  email: string;
  /** Пароль задал администратор: до смены пароля ролей нет. */
  must_change_password: boolean;
  roles: { role: Role; legal_entity_id: string | null; legal_entity: string | null; department_id: string | null; department: string | null }[];
}

export interface Catalog {
  categories: { id: string; name: string; group_name: string | null; is_restricted: boolean }[];
  legalEntities: { id: string; code: string; name: string }[];
  departments: { id: string; code: string; name: string }[];
}

export interface UserRole {
  id: string;
  role: Role;
  legal_entity_id: string | null;
  legal_entity: string | null;
  department_id: string | null;
  department: string | null;
}

export interface AppUser {
  id: string;
  email: string;
  full_name: string;
  is_active: boolean;
  must_change_password: boolean;
  manageable: boolean;
  is_self: boolean;
  roles: UserRole[];
}

export interface Expense {
  id: string;
  op_date: string;
  accrual_month: string;
  amount: string;
  category_id: string;
  category: string;
  category_group: string | null;
  legal_entity_id: string | null;
  legal_entity: string;
  department_id: string | null;
  department: string | null;
  payment_method: 'bank' | 'cash' | null;
  description: string | null;
  comment: string | null;
  source: 'manual' | 'sheet' | 'xlsx_import' | 'api';
  editable: boolean;
}

export interface DayMetric {
  id: string;
  code: string;
  group_name: string;
  name: string;
  unit: 'rub' | 'count' | 'percent';
  value: string | null;
  source: Expense['source'] | null;
  editable: boolean;
}

export interface PlanMetric {
  id: string;
  department_id: string;
  department: string;
  group_name: string;
  name: string;
  unit: 'rub' | 'count' | 'percent';
  value: string | null;
  prev_value: string | null;
  source: Expense['source'] | null;
  editable: boolean;
}

export const ROLE_NAMES: Record<Role, string> = {
  owner: 'Собственник',
  admin: 'Администратор',
  finance: 'Финансист',
  manager: 'Управляющий',
  dept_head: 'Руководитель отдела',
  viewer: 'Наблюдатель',
};

export const SOURCE_NAMES: Record<Expense['source'], string> = {
  manual: 'внесено в МИС',
  api: 'внесено в МИС',
  sheet: 'Google Таблица',
  xlsx_import: 'загружено из xlsx',
};
